import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  FormControlLabel,
  FormGroup,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  geralConferenceService,
  evoWebhookConfigService,
  GeralConferenceConfigResult,
  GeralConferenceExecuteLineResult,
  GeralConferenceExecuteSaleInput,
  GeralConferenceRow,
  EvoWebhookConfig,
} from '../services/firebaseFunctions';

interface AggregatedListResult {
  byConfig: GeralConferenceConfigResult[];
  totalSalesFromEvo: number;
  totalValid: number;
  totalAlreadyProcessed: number;
}

type PageStep = 'idle' | 'listed' | 'executed';

interface EntitlementCsvRow {
  documentId: string;
  evoWebhookConfigId: string;
  idSale: number;
  cpfNormalized: string;
}

interface PendingEntitlementCsvRow {
  documentId: string;
  evoWebhookConfigId: string;
  idSale: number;
  cpfNormalized: string;
}

interface PendingReceivableCsvRow {
  documentId: string;
  evoWebhookConfigId: string;
  idSale: number;
}

interface NotaCsvRow {
  documentId: string;
  evoWebhookConfigId: string;
  idSale: number;
}

const EXECUTE_BATCH_SIZE = 100;

const DATE_CHUNK_DAYS = 5;

/**
 * Quantas empresas processar em paralelo, na listagem e na execução.
 *
 * O limite de 40 req/min da EVO é por credencial (`dns` + `token`), e cada config
 * tem a sua — em produção há 55 configs com 55 tokens distintos. Empresas diferentes
 * portanto não disputam quota entre si e podem correr juntas. O que NÃO pode correr
 * junto é trabalho da MESMA empresa (os períodos de uma listagem, os batches de uma
 * execução), porque aí a credencial é a mesma.
 */
const COMPANY_CONCURRENCY = 4;

/**
 * Executa `worker` sobre `items` com no máximo `limit` em voo.
 *
 * Devolve os resultados na ordem de `items`, independentemente da ordem de conclusão.
 * Um item que rejeita não derruba os outros: a rejeição fica no índice dele.
 */
async function runWithConcurrency<T, R>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>
): Promise<Array<{ ok: true; value: R } | { ok: false; error: unknown }>> {
  const results = new Array<{ ok: true; value: R } | { ok: false; error: unknown }>(items.length);
  let cursor = 0;

  const runner = async (): Promise<void> => {
    for (;;) {
      const index = cursor++;
      if (index >= items.length) return;
      try {
        results[index] = { ok: true, value: await worker(items[index], index) };
      } catch (error) {
        results[index] = { ok: false, error };
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, runner));
  return results;
}

function getCurrentMonthBounds(): { min: string; max: string; firstDay: string; lastDay: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const pad = (n: number) => String(n).padStart(2, '0');
  const firstDay = `${y}-${pad(m + 1)}-01`;
  const lastDay = `${y}-${pad(m + 1)}-${pad(new Date(y, m + 1, 0).getDate())}`;
  return { min: firstDay, max: lastDay, firstDay, lastDay };
}

function buildDateChunks(startYmd: string, endYmd: string, maxDays: number): Array<{ start: string; end: string }> {
  const parse = (s: string) => { const [y, m, d] = s.split('-').map(Number); return Date.UTC(y, m - 1, d); };
  const fmt = (ms: number) => new Date(ms).toISOString().slice(0, 10);
  const result: Array<{ start: string; end: string }> = [];
  let cur = parse(startYmd);
  const end = parse(endYmd);
  while (cur <= end) {
    const chunkEnd = Math.min(cur + (maxDays - 1) * 86_400_000, end);
    result.push({ start: fmt(cur), end: fmt(chunkEnd) });
    cur = chunkEnd + 86_400_000;
  }
  return result;
}

function chunk<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

function escapeCsvCell(value: string | number | boolean | null | undefined): string {
  const s = String(value ?? '');
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildReportCsv(
  results: GeralConferenceExecuteLineResult[],
  companyNameById: Map<string, string>
): string {
  const hasComboSubscription = results.some((r) => r.comboSubscriptionCreated !== undefined);
  const header = ['empresa', 'idSale', 'cpf', 'grantKind', 'pendingReceivableCreated', 'nfCreated', 'nfError', ...(hasComboSubscription ? ['comboSubscriptionCreated'] : []), 'erro'];
  const rows = results.map((r) => [
    companyNameById.get(r.evoWebhookConfigId) ?? r.evoWebhookConfigId,
    r.idSale,
    r.cpfNormalized,
    r.grantKind,
    r.pendingReceivableCreated ? 'sim' : 'não',
    r.nfCreated ? 'sim' : 'não',
    r.nfError ?? '',
    ...(hasComboSubscription ? [r.comboSubscriptionCreated ? 'sim' : 'não'] : []),
    r.error ?? '',
  ]);
  return [header, ...rows].map((row) => row.map(escapeCsvCell).join(',')).join('\n');
}

function buildListCsv(listResult: AggregatedListResult): string {
  const header = [
    'empresa',
    'companyId',
    'evoWebhookConfigId',
    'idSale',
    'idMember',
    'cpf',
    'saleDate',
    'reedyItemValue',
    'hasValidPayment',
    'alreadyProcessed',
    'hasEntitlement',
    'hasPendingEntitlement',
    'hasPendingReceivable',
    'hasPendingDelivery',
    'pendingDeliveryItems',
    'pendingDeliveryQuantity',
    'hasAccount',
    'processable',
  ];
  const rows = listResult.byConfig.flatMap((config) =>
    config.rows.map((row) => [
      config.companyName,
      config.companyId,
      config.evoWebhookConfigId,
      row.idSale,
      row.idMember,
      row.cpfNormalized,
      row.saleDate,
      row.reedyItemValue,
      row.hasValidPayment ? 'sim' : 'não',
      row.alreadyProcessed ? 'sim' : 'não',
      row.hasEntitlement ? 'sim' : 'não',
      row.hasPendingEntitlement ? 'sim' : 'não',
      row.hasPendingReceivable ? 'sim' : 'não',
      row.hasPendingDelivery ? 'sim' : 'não',
      row.pendingDeliveryItems,
      row.pendingDeliveryQuantity,
      row.hasAccount ? 'sim' : 'não',
      canExecuteRow(row) ? 'sim' : 'não',
    ])
  );
  return [header, ...rows].map((row) => row.map(escapeCsvCell).join(',')).join('\n');
}

function canExecuteRow(row: GeralConferenceRow): boolean {
  return !row.hasPendingDelivery && !row.alreadyProcessed && (!row.hasPendingReceivable || row.hasValidPayment);
}

const EvoConferencePage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('conferencia_evo', 'editar');
  const monthBounds = useMemo(() => getCurrentMonthBounds(), []);
  const [searchParams] = useSearchParams();
  const enableDates = searchParams.get('enableDates') === 'true';

  const [step, setStep] = useState<PageStep>('idle');
  const [dateStart, setDateStart] = useState(monthBounds.firstDay);
  const [dateEnd, setDateEnd] = useState(monthBounds.lastDay);

  // Seleção de empresas
  const [availableConfigs, setAvailableConfigs] = useState<EvoWebhookConfig[]>([]);
  const [selectedConfigIds, setSelectedConfigIds] = useState<Set<string>>(new Set());
  const [configsLoading, setConfigsLoading] = useState(true);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [companySearch, setCompanySearch] = useState('');

  useEffect(() => {
    evoWebhookConfigService.getAll().then((configs) => {
      setAvailableConfigs(configs);
      setSelectedConfigIds(new Set(configs.map((c) => c.id)));
    }).catch(() => {}).finally(() => setConfigsLoading(false));
  }, []);

  const [listResult, setListResult] = useState<AggregatedListResult | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listProgress, setListProgress] = useState('');

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  const [executeResults, setExecuteResults] = useState<GeralConferenceExecuteLineResult[]>([]);
  const [executeLoading, setExecuteLoading] = useState(false);
  /**
   * Progresso da execução. Avança a cada batch concluído, ou seja, a cada retorno
   * da callable — a granularidade mais fina possível sem streaming do backend.
   * Como os batches passaram a ser por empresa, na prática avança por empresa.
   */
  const [executeProgress, setExecuteProgress] = useState<{
    sales: number;
    companies: number;
    totalSales: number;
    totalCompanies: number;
  } | null>(null);
  const [createNotas, setCreateNotas] = useState(true);

  const [createdEntitlements, setCreatedEntitlements] = useState<EntitlementCsvRow[]>([]);
  const [createdPendings, setCreatedPendings] = useState<PendingEntitlementCsvRow[]>([]);
  const [createdReceivables, setCreatedReceivables] = useState<PendingReceivableCsvRow[]>([]);
  const [createdNotas, setCreatedNotas] = useState<NotaCsvRow[]>([]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning',
  });

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const saleKey = (configId: string, idSale: number) => `${configId}::${idSale}`;

  const companyNameById = useMemo(() => {
    const map = new Map<string, string>();
    listResult?.byConfig.forEach((c) => map.set(c.evoWebhookConfigId, c.companyName));
    return map;
  }, [listResult]);

  const selectedSales = useMemo<GeralConferenceExecuteSaleInput[]>(() => {
    if (!listResult) return [];
    const result: GeralConferenceExecuteSaleInput[] = [];
    for (const config of listResult.byConfig) {
      for (const row of config.rows) {
        if (selectedKeys.has(saleKey(config.evoWebhookConfigId, row.idSale)) && canExecuteRow(row)) {
          result.push({
            evoWebhookConfigId: config.evoWebhookConfigId,
            idSale: row.idSale,
            idMember: row.idMember,
            cpfNormalized: row.cpfNormalized,
            saleDate: row.saleDate,
            reedyItemValue: row.reedyItemValue,
            hasValidPayment: row.hasValidPayment,
          });
        }
      }
    }
    return result;
  }, [selectedKeys, listResult]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleList = async () => {
    if (!dateStart || !dateEnd) {
      setSnackbar({ open: true, message: 'Informe data início e data fim', severity: 'error' });
      return;
    }
    setListLoading(true);
    setListResult(null);
    setSelectedKeys(new Set());
    setListProgress('Iniciando...');
    try {
      const configs = availableConfigs.filter((c) => selectedConfigIds.has(c.id));
      if (configs.length === 0) {
        setSnackbar({ open: true, message: 'Selecione ao menos uma empresa', severity: 'warning' });
        return;
      }

      const dateChunks = buildDateChunks(dateStart, dateEnd, DATE_CHUNK_DAYS);
      let completedConfigs = 0;
      setListProgress(`0/${configs.length} empresa(s)...`);

      // Empresas em paralelo (credenciais EVO distintas), períodos de uma mesma
      // empresa em série (compartilham a credencial e, portanto, a quota).
      const settled = await runWithConcurrency(configs, COMPANY_CONCURRENCY, async (cfg) => {
        const allRowsById = new Map<number, GeralConferenceRow>();
        let firstResult: GeralConferenceConfigResult | null = null;
        let chunkError: string | undefined;

        for (const { start: ds, end: de } of dateChunks) {
          try {
            const result = await geralConferenceService.listSalesForConfig({
              dateSaleStart: ds,
              dateSaleEnd: de,
              evoWebhookConfigId: cfg.id,
            });
            if (!firstResult) firstResult = result;
            for (const row of result.rows) {
              if (!allRowsById.has(row.idSale)) allRowsById.set(row.idSale, row);
            }
            if (result.error) chunkError = result.error;
          } catch (e) {
            chunkError = e instanceof Error ? e.message : 'Erro';
          }
        }

        completedConfigs += 1;
        setListProgress(`${completedConfigs}/${configs.length} empresa(s)...`);

        return {
          evoWebhookConfigId: cfg.id,
          companyId: firstResult?.companyId ?? cfg.companyId ?? cfg.id,
          companyName: firstResult?.companyName ?? cfg.name ?? cfg.id,
          rows: Array.from(allRowsById.values()),
          error: chunkError,
        } satisfies GeralConferenceConfigResult;
      });

      // `runWithConcurrency` preserva a ordem de `configs`, então a tabela sai
      // na mesma ordem de antes, independentemente de quem terminou primeiro.
      const byConfig: GeralConferenceConfigResult[] = settled.map((outcome, i) =>
        outcome.ok
          ? outcome.value
          : {
              evoWebhookConfigId: configs[i].id,
              companyId: configs[i].companyId ?? configs[i].id,
              companyName: configs[i].name ?? configs[i].id,
              rows: [],
              error: outcome.error instanceof Error ? outcome.error.message : 'Erro',
            }
      );

      const totalSalesFromEvo = byConfig.reduce((acc, c) => acc + c.rows.length, 0);
      const totalValid = totalSalesFromEvo;
      const totalAlreadyProcessed = byConfig.reduce(
        (acc, c) => acc + c.rows.filter((r) => r.alreadyProcessed).length,
        0
      );

      const aggregated: AggregatedListResult = { byConfig, totalSalesFromEvo, totalValid, totalAlreadyProcessed };
      setListResult(aggregated);
      setStep('listed');

      const keys = new Set<string>();
      byConfig.forEach((c) =>
        c.rows.forEach((r) => {
          if (canExecuteRow(r)) keys.add(saleKey(c.evoWebhookConfigId, r.idSale));
        })
      );
      setSelectedKeys(keys);

      const validCount = byConfig.reduce((acc, c) => acc + c.rows.filter(canExecuteRow).length, 0);
      setSnackbar({
        open: true,
        message: `${validCount} venda(s) processáveis encontrada(s) em ${byConfig.length} empresa(s)`,
        severity: 'success',
      });
    } catch (e) {
      setSnackbar({ open: true, message: e instanceof Error ? e.message : 'Erro ao listar vendas', severity: 'error' });
    } finally {
      setListLoading(false);
      setListProgress('');
    }
  };

  const toggleRow = (configId: string, row: GeralConferenceRow) => {
    if (!canExecuteRow(row)) return;
    const key = saleKey(configId, row.idSale);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllInConfig = (configId: string, rows: GeralConferenceRow[]) => {
    const selectableKeys = rows.filter(canExecuteRow).map((r) => saleKey(configId, r.idSale));
    const allSelected = selectableKeys.every((k) => selectedKeys.has(k));
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allSelected) selectableKeys.forEach((k) => next.delete(k));
      else selectableKeys.forEach((k) => next.add(k));
      return next;
    });
  };

  const handleExecute = async () => {
    if (selectedSales.length === 0) return;
    setExecuteLoading(true);
    setExecuteResults([]);
    setCreatedEntitlements([]);
    setCreatedPendings([]);
    setCreatedReceivables([]);
    setCreatedNotas([]);
    try {
      // Agrupar por empresa antes de paralelizar: cada config tem sua própria
      // credencial EVO, logo sua própria quota. Batches misturando empresas
      // impediriam o paralelismo, porque uma invocação carregaria credenciais
      // que competiriam entre si dentro da mesma instância da function.
      const salesByConfig = new Map<string, GeralConferenceExecuteSaleInput[]>();
      for (const sale of selectedSales) {
        const list = salesByConfig.get(sale.evoWebhookConfigId) ?? [];
        list.push(sale);
        salesByConfig.set(sale.evoWebhookConfigId, list);
      }
      const configGroups = Array.from(salesByConfig.values());

      let companiesDone = 0;
      setExecuteProgress({
        sales: 0,
        companies: 0,
        totalSales: selectedSales.length,
        totalCompanies: configGroups.length,
      });

      const allResults: GeralConferenceExecuteLineResult[] = [];
      const entitlements: EntitlementCsvRow[] = [];
      const pendings: PendingEntitlementCsvRow[] = [];
      const receivables: PendingReceivableCsvRow[] = [];
      const notas: NotaCsvRow[] = [];

      // Empresas em paralelo; batches de uma mesma empresa em série.
      const groupOutcomes = await runWithConcurrency(configGroups, COMPANY_CONCURRENCY, async (groupSales) => {
        for (const batch of chunk(groupSales, EXECUTE_BATCH_SIZE)) {
          const result = await geralConferenceService.execute({ sales: batch, createNotas });
          for (const r of result.results) {
            if (r.entitlementId) entitlements.push({ documentId: r.entitlementId, evoWebhookConfigId: r.evoWebhookConfigId, idSale: r.idSale, cpfNormalized: r.cpfNormalized });
            if (r.pendingEntitlementId) pendings.push({ documentId: r.pendingEntitlementId, evoWebhookConfigId: r.evoWebhookConfigId, idSale: r.idSale, cpfNormalized: r.cpfNormalized });
            if (r.pendingReceivableId) receivables.push({ documentId: r.pendingReceivableId, evoWebhookConfigId: r.evoWebhookConfigId, idSale: r.idSale });
            if (r.notaId) notas.push({ documentId: r.notaId, evoWebhookConfigId: r.evoWebhookConfigId, idSale: r.idSale });
          }
          // push + snapshot: o JS é single-threaded entre awaits, então não há
          // corrida entre os grupos concorrentes aqui.
          allResults.push(...result.results);
          setExecuteResults([...allResults]);
          setExecuteProgress({
            sales: allResults.length,
            companies: companiesDone,
            totalSales: selectedSales.length,
            totalCompanies: configGroups.length,
          });
        }

        companiesDone += 1;
        setExecuteProgress({
          sales: allResults.length,
          companies: companiesDone,
          totalSales: selectedSales.length,
          totalCompanies: configGroups.length,
        });
      });

      setCreatedEntitlements(entitlements);
      setCreatedPendings(pendings);
      setCreatedReceivables(receivables);
      setCreatedNotas(notas);

      setStep('executed');
      const errors = allResults.filter((r) => !r.ok).length;
      const blocked = allResults.filter((r) => r.grantKind === 'skipped_pending_delivery').length;
      const successful = allResults.length - errors - blocked;
      // Grupos que falharam por completo (a callable rejeitou) não aparecem em
      // `allResults` — sem isso a execução se reportaria como bem-sucedida.
      const failedGroups = groupOutcomes.filter((o) => !o.ok);
      const unreported = failedGroups.length
        ? ` — ${failedGroups.length} empresa(s) falharam por completo: ${failedGroups
            .map((o) => (!o.ok && o.error instanceof Error ? o.error.message : 'erro'))
            .join('; ')}`
        : '';
      setSnackbar({
        open: true,
        message: (blocked > 0
          ? `Concluído: ${successful} processada(s), ${blocked} bloqueada(s) por entrega pendente${errors > 0 ? `, ${errors} erro(s)` : ''}`
          : errors === 0
            ? `Concluído: ${successful} venda(s) processada(s) com sucesso`
            : `Concluído: ${successful} OK, ${errors} erro(s)`) + unreported,
        severity: blocked > 0 || errors > 0 || failedGroups.length > 0 ? 'warning' : 'success',
      });
    } catch (e) {
      setSnackbar({ open: true, message: e instanceof Error ? e.message : 'Erro ao executar', severity: 'error' });
    } finally {
      setExecuteLoading(false);
      setExecuteProgress(null);
    }
  };

  const handleDownloadReportCsv = () => {
    const csv = buildReportCsv(executeResults, companyNameById);
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evo-conference-${dateStart}-${dateEnd}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadListCsv = () => {
    if (!listResult) return;
    const csv = buildListCsv(listResult);
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evo-conference-listagem-${dateStart}-${dateEnd}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCsv = (rows: object[], filename: string) => {
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const lines = [
      headers.join(','),
      ...rows.map((r) => headers.map((h) => escapeCsvCell((r as Record<string, unknown>)[h] as string | number | boolean | null | undefined)).join(',')),
    ];
    const blob = new Blob(['﻿', lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleReset = () => {
    setStep('idle');
    setListResult(null);
    setSelectedKeys(new Set());
    setExecuteResults([]);
    const { firstDay, lastDay } = getCurrentMonthBounds();
    setDateStart(firstDay);
    setDateEnd(lastDay);
  };


  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Typography variant="h5" gutterBottom>
        Conferência EVO
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Identifica e processa vendas válidas das empresas EVO selecionadas.
      </Typography>

      {/* Seleção de empresas */}
      <Box sx={{ mb: 3 }}>
        {configsLoading ? (
          <CircularProgress size={20} />
        ) : (
          <Button
            variant="outlined"
            size="small"
            onClick={() => { setCompanySearch(''); setCompanyModalOpen(true); }}
            disabled={listLoading || executeLoading}
          >
            {selectedConfigIds.size} empresa(s) selecionada(s)
          </Button>
        )}
      </Box>

      <Dialog open={companyModalOpen} onClose={() => setCompanyModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Selecionar empresas</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar empresa..."
            value={companySearch}
            onChange={(e) => setCompanySearch(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button size="small" onClick={() => setSelectedConfigIds(new Set(availableConfigs.map((c) => c.id)))}>
              Selecionar todos
            </Button>
            <Button size="small" onClick={() => setSelectedConfigIds(new Set())}>
              Limpar
            </Button>
          </Box>
          <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
            <FormGroup>
              {availableConfigs
                .filter((cfg) => {
                  const needle = companySearch
                    .normalize('NFD')
                    .replace(/[̀-ͯ]/g, '')
                    .toLowerCase();
                  const haystack = (cfg.name ?? '')
                    .normalize('NFD')
                    .replace(/[̀-ͯ]/g, '')
                    .toLowerCase();
                  return haystack.includes(needle);
                })
                .map((cfg) => (
                  <FormControlLabel
                    key={cfg.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedConfigIds.has(cfg.id)}
                        onChange={() => {
                          setSelectedConfigIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(cfg.id)) next.delete(cfg.id);
                            else next.add(cfg.id);
                            return next;
                          });
                        }}
                      />
                    }
                    label={<Typography variant="body2">{cfg.name}</Typography>}
                  />
                ))}
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompanyModalOpen(false)} variant="contained">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Passo 1 — Período */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
        <TextField
          label="Data início"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          inputProps={enableDates ? {} : { min: monthBounds.min, max: monthBounds.max }}
          value={dateStart}
          onChange={(e) => setDateStart(e.target.value)}
          disabled={listLoading || executeLoading}
        />
        <TextField
          label="Data fim"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          inputProps={enableDates ? {} : { min: monthBounds.min, max: monthBounds.max }}
          value={dateEnd}
          onChange={(e) => setDateEnd(e.target.value)}
          disabled={listLoading || executeLoading}
        />
        <Button
          variant="contained"
          onClick={handleList}
          disabled={!dateStart || !dateEnd || selectedConfigIds.size === 0 || listLoading || executeLoading}
        >
          {listLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} color="inherit" />
              <span>{listProgress || 'Carregando...'}</span>
            </Box>
          ) : `Identificar vendas (${selectedConfigIds.size} empresa${selectedConfigIds.size !== 1 ? 's' : ''})`}
        </Button>
      </Box>

      {/* Passo 2 — Preview por empresa */}
      {step !== 'idle' && listResult && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip label={`${listResult.totalValid} válidas`} color="primary" size="small" />
            <Chip label={`${listResult.totalAlreadyProcessed} já processadas`} color="default" size="small" />
            {(() => {
              const totalPendingWithoutPayment = listResult.byConfig.reduce(
                (acc, c) =>
                  acc +
                  c.rows.filter(
                    (r) =>
                      r.hasPendingReceivable &&
                      !r.hasPendingDelivery &&
                      !r.hasValidPayment &&
                      !r.hasEntitlement &&
                      !r.hasPendingEntitlement,
                  ).length,
                0,
              );
              return totalPendingWithoutPayment > 0 ? (
                <Chip label={`${totalPendingWithoutPayment} em fila sem pgto`} color="default" size="small" />
              ) : null;
            })()}
            {(() => {
              const totalOrphans = listResult.byConfig.reduce(
                (acc, c) =>
                  acc +
                  c.rows.filter((r) => r.hasPendingReceivable && !r.hasPendingDelivery && !r.alreadyProcessed && r.hasValidPayment).length,
                0,
              );
              return totalOrphans > 0 ? (
                <Chip label={`${totalOrphans} órfã(s) com pgto`} color="warning" size="small" />
              ) : null;
            })()}
            <Chip label={`${listResult.totalSalesFromEvo} da EVO`} color="default" size="small" variant="outlined" />
          </Box>

          {listResult.byConfig.map((config) => {
            const selectableRows = config.rows.filter(canExecuteRow);
            const selectedInConfig = selectableRows.filter((r) =>
              selectedKeys.has(saleKey(config.evoWebhookConfigId, r.idSale))
            ).length;
            const alreadyCount = config.rows.filter((r) => r.alreadyProcessed).length;
            // Venda órfã (bug 009): pagamento confirmado na EVO mas grant nunca aplicado —
            // existe apenas em pendingReceivable. Aparece como selecionável para reprocessamento manual.
            const orphanCount = config.rows.filter(
              (r) => r.hasPendingReceivable && !r.hasPendingDelivery && !r.alreadyProcessed && r.hasValidPayment,
            ).length;
            const pendingWithoutPaymentCount = config.rows.filter(
              (r) =>
                r.hasPendingReceivable &&
                !r.hasPendingDelivery &&
                !r.hasValidPayment &&
                !r.hasEntitlement &&
                !r.hasPendingEntitlement,
            ).length;
            const pendingDeliveryCount = config.rows.filter((r) => r.hasPendingDelivery).length;

            return (
              <Accordion key={config.evoWebhookConfigId} defaultExpanded={selectableRows.length > 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography fontWeight={600}>{config.companyName}</Typography>
                    {config.error && <Chip label={`Erro: ${config.error}`} color="error" size="small" />}
                    <Chip label={`${selectableRows.length} processáveis`} color="primary" size="small" />
                    {alreadyCount > 0 && <Chip label={`${alreadyCount} já processadas`} size="small" />}
                    {pendingWithoutPaymentCount > 0 && (
                      <Chip label={`${pendingWithoutPaymentCount} em fila sem pgto`} size="small" />
                    )}
                    {orphanCount > 0 && (
                      <Chip label={`${orphanCount} órfã(s) com pgto`} color="warning" size="small" />
                    )}
                    {pendingDeliveryCount > 0 && (
                      <Chip label={`${pendingDeliveryCount} com entrega pendente`} color="warning" size="small" />
                    )}
                    {step === 'listed' && selectableRows.length > 0 && (
                      <Chip label={`${selectedInConfig} selecionadas`} color="secondary" size="small" />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {selectableRows.length === 0 ? (
                    <Typography variant="body2" sx={{ p: 2 }} color="text.secondary">
                      Nenhuma venda processável encontrada para esta empresa.
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {step === 'listed' && (
                              <TableCell padding="checkbox">
                                <Checkbox
                                  indeterminate={selectedInConfig > 0 && selectedInConfig < selectableRows.length}
                                  checked={selectableRows.length > 0 && selectedInConfig === selectableRows.length}
                                  onChange={() => toggleAllInConfig(config.evoWebhookConfigId, config.rows)}
                                  disabled={selectableRows.length === 0}
                                />
                              </TableCell>
                            )}
                            <TableCell>Data venda</TableCell>
                            <TableCell>idSale</TableCell>
                            <TableCell>CPF</TableCell>
                            <TableCell>Pagamento</TableCell>
                            <TableCell>Tem conta?</TableCell>
                            <TableCell>Já processado?</TableCell>
                            <TableCell>Filas</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectableRows.map((row) => {
                            const key = saleKey(config.evoWebhookConfigId, row.idSale);
                            const isSelected = selectedKeys.has(key);
                            const canExecute = canExecuteRow(row);
                            const isOrphanWithPayment =
                              row.hasPendingReceivable && !row.hasPendingDelivery && !row.alreadyProcessed && row.hasValidPayment;
                            const rowSx = row.hasPendingDelivery
                              ? { backgroundColor: 'warning.lighter', '&:hover': { backgroundColor: 'warning.light' } }
                              : row.alreadyProcessed
                              ? { opacity: 0.45 }
                              : isOrphanWithPayment
                                ? { backgroundColor: 'warning.lighter', '&:hover': { backgroundColor: 'warning.light' } }
                                : undefined;
                            return (
                              <TableRow key={key} sx={rowSx}>
                                {step === 'listed' && (
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={canExecute && isSelected}
                                      onChange={() => toggleRow(config.evoWebhookConfigId, row)}
                                      disabled={!canExecute}
                                    />
                                  </TableCell>
                                )}
                                <TableCell>{row.saleDate}</TableCell>
                                <TableCell>{row.idSale}</TableCell>
                                <TableCell>{row.cpfNormalized}</TableCell>
                                <TableCell>
                                  <Chip size="small" label={row.hasValidPayment ? 'pago' : 'pendente'} color={row.hasValidPayment ? 'success' : 'warning'} />
                                </TableCell>
                                <TableCell>
                                  <Chip size="small" label={row.hasAccount ? 'sim' : 'não'} color={row.hasAccount ? 'success' : 'default'} />
                                </TableCell>
                                <TableCell>
                                  <Chip size="small" label={row.alreadyProcessed ? 'sim' : 'não'} color={row.alreadyProcessed ? 'warning' : 'default'} />
                                </TableCell>
                                <TableCell>
                                  {row.hasPendingReceivable && (
                                    <Chip
                                      size="small"
                                      label={isOrphanWithPayment ? 'fila c/ pgto' : 'em fila'}
                                      color={isOrphanWithPayment ? 'warning' : 'default'}
                                    />
                                  )}
                                  {row.hasPendingDelivery && (
                                    <Chip
                                      size="small"
                                      label={`entrega pendente: ${row.pendingDeliveryQuantity} título(s)`}
                                      color="warning"
                                      sx={{ ml: row.hasPendingReceivable ? 0.5 : 0 }}
                                    />
                                  )}
                                  {row.hasPendingDelivery && (
                                    <Typography variant="caption" display="block" color="warning.dark">
                                      Trate na tela Fila de entregas.
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}

          {step === 'listed' && (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={createNotas}
                    onChange={(e) => setCreateNotas(e.target.checked)}
                    disabled={executeLoading}
                  />
                }
                label="Criar notas para serem enviadas"
                sx={{ mb: 1, display: 'block' }}
              />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  onClick={handleDownloadListCsv}
                  disabled={!listResult || listResult.totalValid === 0 || executeLoading}
                >
                  Baixar CSV da listagem
                </Button>
                <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Conferência EVO.'}>
                  <span>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleExecute}
                      disabled={!canManage || selectedSales.length === 0 || executeLoading}
                    >
                      {executeLoading
                        ? <CircularProgress size={22} color="inherit" />
                        : `Confirmar e processar (${selectedSales.length} selecionadas)`}
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              {executeProgress && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {executeProgress.companies}/{executeProgress.totalCompanies} empresa(s) ·{' '}
                      {executeProgress.sales}/{executeProgress.totalSales} venda(s)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {executeProgress.totalSales > 0
                        ? Math.round((executeProgress.sales / executeProgress.totalSales) * 100)
                        : 0}
                      %
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      executeProgress.totalSales > 0
                        ? Math.min((executeProgress.sales / executeProgress.totalSales) * 100, 100)
                        : 0
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Até {COMPANY_CONCURRENCY} empresas em paralelo. Não feche a página.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Passo 3 — Relatório */}
      {step === 'executed' && executeResults.length > 0 && (
        <Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>Relatório</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip label={`${executeResults.filter((r) => r.ok && r.grantKind !== 'skipped_pending_delivery').length} OK`} color="success" size="small" />
            {executeResults.some((r) => r.grantKind === 'skipped_pending_delivery') && (
              <Chip label={`${executeResults.filter((r) => r.grantKind === 'skipped_pending_delivery').length} bloqueada(s) por entrega pendente`} color="warning" size="small" />
            )}
            <Chip label={`${executeResults.filter((r) => !r.ok).length} erros`} color={executeResults.some((r) => !r.ok) ? 'error' : 'default'} size="small" />
          </Box>

          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Empresa</TableCell>
                  <TableCell>idSale</TableCell>
                  <TableCell>CPF</TableCell>
                  <TableCell>Grant</TableCell>
                  <TableCell>Pend.Rec</TableCell>
                  <TableCell>NF</TableCell>
                  {executeResults.some((r) => r.comboSubscriptionCreated !== undefined) && (
                    <TableCell>Assinatura</TableCell>
                  )}
                  <TableCell>Erro</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {executeResults.map((r, i) => (
                  <TableRow key={i} sx={!r.ok ? { bgcolor: 'error.lighter' } : r.grantKind === 'skipped_pending_delivery' ? { bgcolor: 'warning.lighter' } : undefined}>
                    <TableCell>{companyNameById.get(r.evoWebhookConfigId) ?? r.evoWebhookConfigId}</TableCell>
                    <TableCell>{r.idSale}</TableCell>
                    <TableCell>{r.cpfNormalized}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.grantKind}
                        color={r.grantKind === 'entitlement_created' || r.grantKind === 'pending_created' ? 'success' : r.grantKind === 'skipped_pending_delivery' ? 'warning' : r.grantKind === 'error' ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.pendingReceivableCreated ? 'sim' : 'não'} color={r.pendingReceivableCreated ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.nfCreated ? 'sim' : r.nfError ? 'erro' : 'não'} color={r.nfCreated ? 'success' : r.nfError ? 'warning' : 'default'} />
                    </TableCell>
                    {executeResults.some((res) => res.comboSubscriptionCreated !== undefined) && (
                      <TableCell>
                        {r.comboSubscriptionCreated !== undefined
                          ? <Chip size="small" label={r.comboSubscriptionCreated ? 'sim' : 'não'} color={r.comboSubscriptionCreated ? 'success' : 'default'} />
                          : '—'}
                      </TableCell>
                    )}
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.error || r.nfError || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={handleDownloadReportCsv}>
              Baixar CSV do relatório
            </Button>
            {createdEntitlements.length > 0 && (
              <Button variant="outlined" color="success" onClick={() => downloadCsv(createdEntitlements, `entitlements-${dateStart}-${dateEnd}.csv`)}>
                Entitlements ({createdEntitlements.length})
              </Button>
            )}
            {createdPendings.length > 0 && (
              <Button variant="outlined" color="success" onClick={() => downloadCsv(createdPendings, `pending-entitlements-${dateStart}-${dateEnd}.csv`)}>
                Pending Entitlements ({createdPendings.length})
              </Button>
            )}
            {createdReceivables.length > 0 && (
              <Button variant="outlined" color="warning" onClick={() => downloadCsv(createdReceivables, `pending-receivables-${dateStart}-${dateEnd}.csv`)}>
                Pending Receivables ({createdReceivables.length})
              </Button>
            )}
            {createdNotas.length > 0 && (
              <Button variant="outlined" color="warning" onClick={() => downloadCsv(createdNotas, `notas-nao-enviadas-${dateStart}-${dateEnd}.csv`)}>
                Notas Não Enviadas ({createdNotas.length})
              </Button>
            )}
            <Button variant="text" onClick={handleReset}>
              Nova consulta
            </Button>
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EvoConferencePage;
